import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";
import type { RecommendTopic, RecommendHub } from "@/content/recommend-corpus/types";

export const Route = createFileRoute("/recommend/$hub/")({
  loader: async ({ params }) => {
    const { getRecommendHub } = await import("@/content/recommend-corpus/hubs");
    const { loadHubTopics, loadHubKeywords } = await import("@/content/recommend-corpus/hub-lazy");
    const hub = getRecommendHub(params.hub);
    if (!hub) throw notFound();
    const [topics, keywords] = await Promise.all([loadHubTopics(hub.slug), loadHubKeywords(hub.slug)]);
    return { hub, topics, keywords: keywords.slice(0, 40) };
  },
  component: RecommendHubPage,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "ไม่พบหมวดแนะนำบริการ" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { hub } = loaderData;
    return {
      ...pageHeadForProvince({
        title: hub.metaTitle,
        description: hub.metaDesc,
        path: `/recommend/${params.hub}`,
        type: "article",
        keywords: loaderData.keywords.slice(0, 25),
      }),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/recommend/${params.hub}`)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "แนะนำบริการ", url: `${site}/recommend` },
              { name: hub.titleTh, url: `${site}/recommend/${params.hub}` },
            ]),
          ),
        },
      ],
    };
  },
});

function RecommendHubPage() {
  const { hub, topics, keywords } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/recommend" className="hover:underline">แนะนำบริการ</a> <span>/</span> <span>{hub.titleTh}</span>
        </nav>
        <h1 className="mb-4 text-3xl font-bold md:text-4xl">{hub.h1}</h1>
        <p className="speakable-summary mb-6 rounded-lg border bg-muted/30 p-5 text-sm leading-relaxed">{hub.quickAnswer}</p>
        {hub.intro.map((p: string, i: number) => (
          <p key={i} className="mb-4 leading-relaxed text-muted-foreground">{p}</p>
        ))}

        {topics.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-4 text-2xl font-bold">เลือกตามกลุ่มงานที่ท่านต้องใช้</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {(topics as RecommendTopic[]).map((t: RecommendTopic) => (
                <a key={t.slug} href={`/recommend/${hub.slug}/${t.slug}`} className="rounded-lg border bg-card p-5 hover:shadow-sm">
                  <h3 className="mb-2 font-semibold">{t.titleTh}</h3>
                  <p className="text-sm text-muted-foreground">{t.metaDesc}</p>
                </a>
              ))}
            </div>
          </section>
        )}

        <section className="mt-10">
          <h2 className="mb-3 text-2xl font-bold">บริการที่เกี่ยวข้อง</h2>
          <ul className="list-inside list-disc space-y-1 text-sm">
            {(hub as RecommendHub).serviceLinks.map((s: { label: string; href: string }) => (
              <li key={s.href}>
                <a href={s.href} className="text-primary underline">{s.label}</a>
              </li>
            ))}
          </ul>
        </section>

        {keywords.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-3 text-2xl font-bold">คำค้นที่ผู้ใช้บริการถามถึงบ่อยในหมวดนี้</h2>
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              {keywords.map((k: string) => (
                <span key={k} className="rounded-full border bg-muted/40 px-3 py-1">{k}</span>
              ))}
            </div>
          </section>
        )}

        <section className="mt-12 rounded-lg border bg-primary/5 p-6">
          <h2 className="mb-2 text-xl font-bold">ไม่แน่ใจว่ากรณีของท่านต้องใช้เส้นทางใด</h2>
          <p className="mb-4 text-sm text-muted-foreground">ส่งรายการเอกสารและปลายทางให้ทีมงานตรวจให้ก่อนเริ่มงาน</p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">ติดต่อทีมงาน →</a>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
